import clientStorage from '@/services/ClientStorage';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

/**
 * State backed by local storage.
 *
 * Setting `null` forgets the stored value rather than saving an empty one, so a
 * "reset to defaults" leaves nothing behind to be read back on the next load.
 */
export const useStoredState = <S>(
    storageName: string | string[],
    initialState: S = undefined
): [S, Dispatch<SetStateAction<S>>] => {
    const [value, setValue] = useState<S>();

    useEffect(() => {
        const data = clientStorage.get<S>(storageName);
        if (data !== undefined) {
            setValue(data);
        }
    }, []);

    useEffect(() => {
        if (value === null) {
            // Setting null forgets the value rather than storing an empty one
            clientStorage.remove(storageName);
        } else if (value !== undefined) {
            clientStorage.set(storageName, value);
        }
    }, [value]);

    return [value ?? initialState, setValue];
};
